/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
	Box,
	Paper,
	Typography,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	IconButton,
	Tooltip,
	OutlinedInput,
	SelectChangeEvent,
	Badge,
} from '@mui/material';
import { FilterList as FilterListIcon, Clear as ClearIcon } from '@mui/icons-material';
import { IFlightAndFlightAncillaryIssueSummary } from '../../../types';

export interface LocalFiltersState {
	workflows: string[];
	stages: string[];
	agents: string[];
}

interface LocalFiltersProps {
	data: IFlightAndFlightAncillaryIssueSummary[];
	onFiltersChange: (filters: LocalFiltersState) => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

export const LocalFilters: React.FC<LocalFiltersProps> = ({ data, onFiltersChange }) => {
	const [filters, setFilters] = useState<LocalFiltersState>({
		workflows: [],
		stages: [],
		agents: [],
	});

	// Extraer valores únicos de los datos actuales
	const uniqueValues = useMemo(() => {
		const workflows = new Set<string>();
		const stages = new Set<string>();
		const agents = new Set<string>();

		data.forEach((item) => {
			if (item.crmWorkflowName?.trim()) {
				workflows.add(item.crmWorkflowName.trim());
			}
			if (item.crmSteepName?.trim()) {
				stages.add(item.crmSteepName.trim());
			}
			if (item.crmAgentCompleteName?.trim()) {
				agents.add(item.crmAgentCompleteName.trim());
			}
		});

		return {
			workflows: Array.from(workflows).sort(),
			stages: Array.from(stages).sort(),
			agents: Array.from(agents).sort(),
		};
	}, [data]);

	// Notificar cambios al componente padre
	useEffect(() => {
		onFiltersChange(filters);
	}, [filters, onFiltersChange]);

	const handleFilterChange = (field: keyof LocalFiltersState, event: SelectChangeEvent<string[]>) => {
		const value = event.target.value as string[];
		setFilters((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleClearFilter = (field: keyof LocalFiltersState) => {
		setFilters((prev) => ({
			...prev,
			[field]: [],
		}));
	};

	const handleClearAll = () => {
		setFilters({
			workflows: [],
			stages: [],
			agents: [],
		});
	};

	const getActiveFiltersCount = () => {
		return filters.workflows.length + filters.stages.length + filters.agents.length;
	};

	const activeFiltersCount = getActiveFiltersCount();

	return (
		<Paper
			elevation={1}
			sx={{
				p: 2,
				borderRadius: 2,
				border: '1px solid #e0e0e0',
				backgroundColor: '#fafafa',
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
				<Box display="flex" alignItems="center" gap={1}>
					<Badge badgeContent={activeFiltersCount} color="primary">
						<FilterListIcon color="action" />
					</Badge>
					<Typography variant="subtitle1" fontWeight={500} component="h3">
						Filtros en pantalla:
					</Typography>
				</Box>

				<Box display="flex" alignItems="center" gap={1}>
					<Typography variant="body2" color="text.secondary" fontStyle="italic">
						*únicamente sobre resultados que se visualizan en pantalla
					</Typography>
					{activeFiltersCount > 0 && (
						<Tooltip title="Limpiar todos los filtros">
							<IconButton size="small" onClick={handleClearAll}>
								<ClearIcon />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			</Box>

			<Grid container spacing={2} alignItems="center">
				{/* Workflow Filter */}
				<Grid item xs={12} md={4}>
					<FormControl fullWidth size="small">
						<InputLabel>Workflow</InputLabel>
						<Select
							multiple
							value={filters.workflows}
							onChange={(e) => handleFilterChange('workflows', e)}
							input={<OutlinedInput label="Workflow" />}
							MenuProps={MenuProps}
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((value) => (
										<Chip key={value} label={value} size="small" />
									))}
								</Box>
							)}
							disabled={uniqueValues.workflows.length === 0}
						>
							{uniqueValues.workflows.map((workflow) => (
								<MenuItem key={workflow} value={workflow}>
									{workflow}
								</MenuItem>
							))}
						</Select>
						{filters.workflows.length > 0 && (
							<Tooltip title="Limpiar Workflow">
								<IconButton
									size="small"
									sx={{ position: 'absolute', right: 32, top: 8 }}
									onClick={() => handleClearFilter('workflows')}
								>
									<ClearIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					</FormControl>
				</Grid>

				{/* Stage Filter */}
				<Grid item xs={12} md={4}>
					<FormControl fullWidth size="small">
						<InputLabel>Etapa</InputLabel>
						<Select
							multiple
							value={filters.stages}
							onChange={(e) => handleFilterChange('stages', e)}
							input={<OutlinedInput label="Etapa" />}
							MenuProps={MenuProps}
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((value) => (
										<Chip
											key={value}
											label={value}
											size="small"
											color={value === 'Done' ? 'success' : value === 'Fail' ? 'error' : 'default'}
										/>
									))}
								</Box>
							)}
							disabled={uniqueValues.stages.length === 0}
						>
							{uniqueValues.stages.map((stage) => (
								<MenuItem key={stage} value={stage}>
									{stage}
								</MenuItem>
							))}
						</Select>
						{filters.stages.length > 0 && (
							<Tooltip title="Limpiar Etapa">
								<IconButton
									size="small"
									sx={{ position: 'absolute', right: 32, top: 8 }}
									onClick={() => handleClearFilter('stages')}
								>
									<ClearIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					</FormControl>
				</Grid>

				{/* Agent Filter */}
				<Grid item xs={12} md={4}>
					<FormControl fullWidth size="small">
						<InputLabel>Agente</InputLabel>
						<Select
							multiple
							value={filters.agents}
							onChange={(e) => handleFilterChange('agents', e)}
							input={<OutlinedInput label="Agente" />}
							MenuProps={MenuProps}
							renderValue={(selected) => (
								<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
									{selected.map((value) => (
										<Chip key={value} label={value} size="small" />
									))}
								</Box>
							)}
							disabled={uniqueValues.agents.length === 0}
						>
							{uniqueValues.agents.map((agent) => (
								<MenuItem key={agent} value={agent}>
									{agent}
								</MenuItem>
							))}
						</Select>
						{filters.agents.length > 0 && (
							<Tooltip title="Limpiar Agente">
								<IconButton
									size="small"
									sx={{ position: 'absolute', right: 32, top: 8 }}
									onClick={() => handleClearFilter('agents')}
								>
									<ClearIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						)}
					</FormControl>
				</Grid>
			</Grid>
		</Paper>
	);
};
